import {Box, Button, CircularProgress, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import type {AggregationSurveyEvaluationAnswerRow}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import type {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {SurveyAggregationService} from "crqlar-react-common/dist/services/aggregation/surveyAggregation.service";
import {useContext, useId, useState} from "react";
import {useTranslation} from "react-i18next";
import {useInfiniteQuery} from "react-query";
import {SURVEY_EVALUATION_QUERY_KEY} from "../shared/surveyQueryRefresh";
import {buildSurveyEvaluationRequest, SurveyEvaluationScopeContext} from "../shared/useSurveyEvaluation";
import SurveyEvaluationSubmissionDialog from "./SurveyEvaluationSubmissionDialog";
import {isCommentQuestion} from "./questionSummary.util";
import SurveyEvaluationCommentRating from "./SurveyEvaluationCommentRating";

const PAGE_SIZE = 25;

interface Props {
    question: SurveyQuestion
    period: 'current' | 'compare'
    includeSensitive: boolean
}

function WrittenAnswerRow({row, showCommentRating, onOpen}: {row: AggregationSurveyEvaluationAnswerRow, showCommentRating: boolean, onOpen: () => void}) {
    const {t} = useTranslation();
    const textId = useId();
    const ratingId = useId();
    const name = row.profileName?.trim() || (row.profileId ? t('Unknown guest') : t('Anonymous guest'));

    return (
        <Box
            role={'button'}
            tabIndex={0}
            aria-label={`${t('View submission')}: ${name}`}
            aria-describedby={showCommentRating ? `${ratingId} ${textId}` : textId}
            onClick={event => {
                // Selecting the answer to read or copy it must not open its response.
                const selection = window.getSelection();
                if (selection?.toString() && event.currentTarget.contains(selection.anchorNode)) return;
                onOpen();
            }}
            onKeyDown={event => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                onOpen();
            }}
            sx={theme => ({
                display: 'grid',
                gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: '140px minmax(0, 1fr)'},
                columnGap: 2,
                rowGap: 0.5,
                alignItems: 'start',
                minWidth: 0,
                px: 1,
                py: 1.25,
                cursor: 'pointer',
                borderRadius: 1,
                '&:hover': {bgcolor: 'action.hover'},
                '&:focus-visible': {outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2},
            })}
        >
            <Stack gap={0.5} sx={{minWidth: 0}}>
                <Typography variant={'body2'} color={'text.secondary'} noWrap sx={{minWidth: 0, fontWeight: 600}}>
                    {name}
                </Typography>
                {showCommentRating && <SurveyEvaluationCommentRating id={ratingId} sentiment={row.sentiment} score={row.commentScore}/>}
            </Stack>

            <Stack id={textId} gap={0.5} sx={{minWidth: 0}}>
                {row.answerTexts.map((text, index) => (
                    <Typography
                        key={index}
                        variant={'body1'}
                        sx={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}
                    >
                        {text}
                    </Typography>
                ))}
            </Stack>
        </Box>
    );
}

/** Attributed, readable answers to one question in one of the evaluation's periods. */
export default function SurveyEvaluationWrittenAnswers({question, period, includeSensitive}: Props) {
    const {t} = useTranslation();
    const scope = useContext(SurveyEvaluationScopeContext);
    const surveyId = scope?.surveyId;
    const questionId = question.id;
    const showCommentRating = isCommentQuestion(question) && !question.isSensitive;
    const range = period === 'compare' ? scope?.filters.compareRange : scope?.filters.range;
    const [submissionId, setSubmissionId] = useState<string>();

    const answers = useInfiniteQuery(
        [SURVEY_EVALUATION_QUERY_KEY, 'written', surveyId, scope?.filters, questionId, period, includeSensitive, showCommentRating],
        ({pageParam = 0}) => SurveyAggregationService.listEvaluationAnswers({
            evaluation: buildSurveyEvaluationRequest(
                surveyId!, {...scope!.filters, range: range!, compareRange: null}, includeSensitive
            ),
            questionId: questionId!,
            writtenOnly: true,
            limit: {
                limit: PAGE_SIZE,
                offset: pageParam,
                sort: [{field: showCommentRating ? 'commentScore' : 'submittedAt', direction: showCommentRating ? 'ASC' : 'DESC'}],
            },
        }),
        {
            enabled: !!surveyId && !!questionId && !!range,
            refetchOnWindowFocus: false,
            getNextPageParam: (last, pages) => {
                const loaded = pages.reduce((sum, page) => sum + page.content.length, 0);
                return loaded < last.totalElements ? loaded : undefined;
            },
        }
    );
    const rows = answers.data?.pages.flatMap(page => page.content) ?? [];

    return (
        <>
            {answers.isLoading ? (
                <Stack alignItems={'center'} sx={{py: 3}}><CircularProgress size={20}/></Stack>
            ) : answers.isError ? (
                <Stack alignItems={'center'} gap={1} sx={{py: 3}}>
                    <Typography variant={'body2'} color={'text.secondary'}>{t('Could not load the answers.')}</Typography>
                    <Button onClick={() => answers.refetch()}>{t('Try again')}</Button>
                </Stack>
            ) : !rows.length ? (
                <Typography variant={'body2'} color={'text.secondary'}>{t('No answers in this period.')}</Typography>
            ) : (
                <Stack gap={0.5}>
                    {rows.map(row => (
                        <WrittenAnswerRow key={row.submissionId} row={row} showCommentRating={showCommentRating} onOpen={() => setSubmissionId(row.submissionId)}/>
                    ))}
                    {answers.hasNextPage && (
                        <Button
                            onClick={() => answers.fetchNextPage()}
                            disabled={answers.isFetchingNextPage}
                            sx={{alignSelf: 'center'}}
                            startIcon={answers.isFetchingNextPage ? <CircularProgress size={16} color={'inherit'}/> : undefined}
                        >
                            {t('Load more')}
                        </Button>
                    )}
                </Stack>
            )}

            {submissionId && (
                <SurveyEvaluationSubmissionDialog
                    submissionId={submissionId}
                    questionId={questionId!}
                    onClose={() => setSubmissionId(undefined)}
                />
            )}
        </>
    );
}
